import { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import { format } from "date-fns";
import { useLiveTickerData, type TickerItem, type TickerEditorial } from "@/hooks/useLiveTickerData";
import { EditorialFallbackRail } from "@/components/ticker/EditorialFallbackRail";
import { useTickerGoalEvents, type GoalAlertEvent } from "@/hooks/useTickerGoalEvents";
import { GoalTickerItem } from "@/components/ticker/GoalTickerItem";
import { Skeleton } from "@/components/ui/skeleton";
import { WORLD_CUP_START, WORLD_CUP_OFFICIAL_LOGO } from "@/lib/worldCup2026";

const WC_KICKOFF_MS = new Date(`${WORLD_CUP_START}T20:00:00Z`).getTime();

function wcDiff() {
  const ms = WC_KICKOFF_MS - Date.now();
  if (ms <= 0) return null;
  return {
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000),
  };
}

function WorldCupCountdownTickerItem() {
  const [cd, setCd] = useState(() => wcDiff());
  const navigate = useNavigate();
  useEffect(() => {
    const t = setInterval(() => setCd(wcDiff()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!cd) return null;
  const label =
    cd.d === 0
      ? `KICKS OFF IN ${cd.h}h ${cd.m}m`
      : `${cd.d}d ${cd.h}h ${cd.m}m`;
  return (
    <button
      onClick={() => navigate("/world-cup")}
      className="inline-flex items-center gap-2 px-3 py-1 rounded whitespace-nowrap flex-shrink-0 bg-[hsl(220_60%_15%)] shadow-[0_0_12px_rgba(255,200,50,0.15)] hover:bg-[hsl(220_60%_20%)] transition-colors cursor-pointer"
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(255,0,0,0.18) 0%, rgba(60,59,110,0.22) 50%, rgba(0,104,71,0.18) 100%)",
      }}
    >
      <img src={WORLD_CUP_OFFICIAL_LOGO} alt="" className="w-4 h-4 object-contain flex-shrink-0" loading="lazy" />
      <span className="text-[11px] font-black uppercase tracking-wider text-white">
        FIFA WORLD CUP 2026
      </span>
      <span className="text-[hsl(0_0%_50%)] text-[10px] flex-shrink-0 select-none" aria-hidden>•</span>
      <span className="text-[12px] font-bold tabular-nums" style={{ color: "rgba(255,200,50,0.95)" }}>
        {label}
      </span>
    </button>
  );
}

interface LiveTickerProps {
  onSelectGame: (game: any) => void;
}

interface ScoreHighlight {
  side: "home" | "away";
  expiresAt: number;
}

function StatusBadge({ item }: { item: TickerItem }) {
  switch (item.status) {
    case "live":
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-[hsl(var(--sg7-risk-high))] text-white animate-[live-badge-pulse_2.5s_ease-in-out_infinite]">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-50" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
          </span>
          LIVE
        </span>
      );
    case "ht":
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-[hsl(45_93%_47%)] text-[hsl(0_0%_8%)]">
          HT
        </span>
      );
    case "ft":
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-[hsl(0_0%_30%)] text-[hsl(0_0%_75%)]">
          FT
        </span>
      );
    case "upcoming":
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[hsl(142_40%_16%)] text-[hsl(142_70%_55%)]">
          <Clock className="w-2.5 h-2.5" />
          {format(new Date(item.match_date), "HH:mm")}
        </span>
      );
  }
}

function TeamLogo({ src, alt }: { src: string; alt: string }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className="w-3.5 h-3.5 object-contain flex-shrink-0"
      loading="lazy"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

const SCORE_GLOW_CLASS = "animate-[score-glow-pulse_3s_ease-in-out_infinite]";

const EDITORIAL_STYLES: Record<string, { bg: string; glow?: string }> = {
  ucl_day:     { bg: "bg-[hsl(235_70%_30%)]", glow: "shadow-[0_0_12px_rgba(26,35,126,0.4)]" },
  ucl_summary: { bg: "bg-[hsl(220_60%_15%)]" },
  uel_day:     { bg: "bg-[hsl(30_100%_45%)]", glow: "shadow-[0_0_12px_rgba(255,165,0,0.5)]" },
  uel_summary: { bg: "bg-[hsl(28_90%_22%)]" },
  uecl_day:    { bg: "bg-[hsl(150_50%_18%)]", glow: "shadow-[0_0_12px_rgba(30,120,70,0.4)]" },
  uecl_summary:{ bg: "bg-[hsl(150_35%_12%)]" },
};

function EditorialTickerItem({ editorial }: { editorial: TickerEditorial }) {
  const style = EDITORIAL_STYLES[editorial.type] || EDITORIAL_STYLES.ucl_summary;
  const altText = editorial.type.includes("ucl") ? "Champions League" : editorial.type.includes("uel") ? "Europa League" : "Conference League";
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 px-3 py-1 rounded whitespace-nowrap flex-shrink-0",
        style.bg,
        style.glow
      )}
    >
      {editorial.leagueLogo && (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/90 flex-shrink-0">
          <img
            src={editorial.leagueLogo}
            alt={altText}
            className="w-3.5 h-3.5 object-contain"
            loading="lazy"
          />
        </span>
      )}
      <span className="text-[11px] font-black uppercase tracking-wider text-white animate-[editorial-badge-pulse_2.5s_ease-in-out_infinite]">
        {editorial.badge}
      </span>
      <span className="text-[hsl(0_0%_50%)] text-[10px] flex-shrink-0 select-none" aria-hidden>•</span>
      <span className="text-[12px] font-medium text-[hsl(0_0%_80%)]">
        {editorial.text}
      </span>
    </div>
  );
}

function TickerItemView({
  item,
  onClick,
  highlightSide,
}: {
  item: TickerItem;
  onClick: () => void;
  highlightSide?: "home" | "away" | null;
}) {
  // Editorial items render differently
  if (item.editorial) {
    return <EditorialTickerItem editorial={item.editorial} />;
  }

  const showScore = item.status === "live" || item.status === "ht" || item.status === "ft";
  const showMinute = item.status === "live" && item.elapsed;

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-1 py-1 rounded hover:bg-[hsl(0_0%_18%)] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
    >
      {(() => {
        const apiId = item.game?.league?.api_football_id;
        if (!apiId || ![1, 2, 3, 848].includes(apiId)) return null;
        const isWC = apiId === 1;
        const src = isWC ? WORLD_CUP_OFFICIAL_LOGO : item.game?.league?.logo_url;
        if (!src) return null;
        const alt = isWC ? "FIFA World Cup" : "UCL";
        return (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/90 flex-shrink-0">
            <img src={src} alt={alt} className="w-3.5 h-3.5 object-contain" loading="lazy" />
          </span>
        );
      })()}
      <StatusBadge item={item} />
      <span className="inline-flex items-center gap-1 text-[13px] font-bold text-[hsl(0_0%_92%)]">
        <TeamLogo src={item.home_team_logo} alt={item.home_team} />
        {showScore ? (
          <>
            {item.home_team}
            <span className="mx-1 font-black">
              <span className={cn("text-[hsl(45_93%_47%)]", highlightSide === "home" && SCORE_GLOW_CLASS)}>
                {item.home_score}
              </span>
              <span className="text-[hsl(45_93%_47%)]">–</span>
              <span className={cn("text-[hsl(45_93%_47%)]", highlightSide === "away" && SCORE_GLOW_CLASS)}>
                {item.away_score}
              </span>
            </span>
            {item.away_team}
          </>
        ) : (
          <>
            {item.home_team}
            <span className="mx-1 text-[hsl(0_0%_45%)]">vs</span>
            {item.away_team}
          </>
        )}
        <TeamLogo src={item.away_team_logo} alt={item.away_team} />
      </span>
      {showMinute && (
        <span className="text-[11px] font-bold text-[hsl(0_84%_60%)]">
          {item.elapsed}'
        </span>
      )}
    </button>
  );
}

const SEPARATOR = (
  <span className="text-[hsl(0_0%_30%)] text-[10px] mx-2 flex-shrink-0 select-none" aria-hidden>
    ◆
  </span>
);

/**
 * Determine which side scored based on goal's team_id and ticker item's game data.
 */
function resolveSide(goal: GoalAlertEvent, items: TickerItem[]): "home" | "away" | null {
  if (!goal.team_id) return null;
  const match = items.find((i) => i.fixture_id === goal.fixture_id);
  if (!match?.game) return null;
  const homeId = match.game.home_team?.id;
  const awayId = match.game.away_team?.id;
  if (goal.team_id === homeId) return "home";
  if (goal.team_id === awayId) return "away";
  return null;
}

export function LiveTicker({ onSelectGame }: LiveTickerProps) {
  const { data: tickerResult, isLoading, isError } = useLiveTickerData();
  const items = tickerResult?.items;
  const fallbackMode = tickerResult?.fallbackMode ?? false;
  const nextFixtureDate = tickerResult?.nextFixtureDate ?? null;
  const { activeGoal, isGoalLock } = useTickerGoalEvents();
  const [isPaused, setIsPaused] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [scoreHighlights, setScoreHighlights] = useState<Map<number, ScoreHighlight>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const prevGoalRef = useRef<GoalAlertEvent | null>(null);
  const navigate = useNavigate();

  // Sticky detection
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([e]) => setIsStuck(!e.isIntersecting),
      { threshold: [1] }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Track goal transitions: when activeGoal goes from a value to null, add highlight
  useEffect(() => {
    if (activeGoal) {
      prevGoalRef.current = activeGoal;
    } else if (prevGoalRef.current && items) {
      // Goal lock just ended — create score highlight
      const goal = prevGoalRef.current;
      const side = resolveSide(goal, items);
      if (side) {
        setScoreHighlights((prev) => {
          const next = new Map(prev);
          next.set(goal.fixture_id, { side, expiresAt: Date.now() + 60_000 });
          return next;
        });
      }
      prevGoalRef.current = null;
    }
  }, [activeGoal, items]);

  // Cleanup expired highlights every 5s
  useEffect(() => {
    if (scoreHighlights.size === 0) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setScoreHighlights((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [key, val] of next) {
          if (val.expiresAt <= now) {
            next.delete(key);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [scoreHighlights.size]);

  const handleSelect = useCallback(
    (item: TickerItem) => {
      if (item.editorial) return; // Editorial items are non-clickable
      if (item.status === "live" || item.status === "ft" || item.status === "ht") {
        navigate(`/game/${item.fixture_id}`);
      } else {
        onSelectGame(item.game);
      }
    },
    [onSelectGame, navigate]
  );

  // Hide on error or empty
  if (isError) return null;

  if (isLoading) {
    return (
      <div className="sticky top-0 z-30 bg-[hsl(0_0%_10%)] border-b border-[hsl(0_0%_20%)]">
        <div className="h-10 flex items-center px-4">
          <Skeleton className="h-4 w-full bg-[hsl(0_0%_18%)]" />
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    if (fallbackMode) {
      return <EditorialFallbackRail nextFixtureDate={nextFixtureDate} />;
    }
    return null;
  }

  // Dynamic repetition: ensure content always exceeds 2x viewport for seamless loop
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1400;
  const estItemWidth = 200; // ~200px per ticker item
  const singleSetWidth = items.length * estItemWidth;
  const minCopies = Math.max(2, Math.ceil((viewportWidth * 2) / singleSetWidth) + 1);

  // Duration scales with single set width at ~45px/s
  const duration = Math.max(singleSetWidth / 45, 15);
  // Loop offset: scroll exactly one copy's width, then jump back
  const loopOffset = `-${(100 / minCopies).toFixed(4)}%`;

  const renderItems = (keyPrefix: string) =>
    items.map((item, i) => {
      const hl = scoreHighlights.get(item.fixture_id);
      return (
        <span key={`${keyPrefix}-${item.fixture_id}-${i}`} className="inline-flex items-center">
          {i > 0 && SEPARATOR}
          <TickerItemView
            item={item}
            onClick={() => handleSelect(item)}
            highlightSide={hl ? hl.side : null}
          />
        </span>
      );
    });

  return (
    <>
    <div ref={sentinelRef} className="h-0 w-full" aria-hidden />
    <div
      className={cn(
        "sticky top-0 z-30 bg-[hsl(var(--sg7-surface-1))] border-b overflow-hidden transition-all duration-300",
        isStuck
          ? "border-[hsl(var(--sg7-stroke-subtle))] shadow-[0_2px_12px_rgba(0,0,0,0.5)]"
          : "border-[hsl(var(--sg7-stroke-subtle)/0.5)] shadow-none"
      )}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      ref={containerRef}
    >
      {/* Left fade */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[hsl(0_0%_10%)] to-transparent z-10 pointer-events-none" />
      {/* Right fade */}
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[hsl(0_0%_10%)] to-transparent z-10 pointer-events-none" />

      {isGoalLock && activeGoal && items ? (
        <GoalTickerItem goal={activeGoal} tickerItems={items} />
      ) : (
        <div
          className="flex items-center h-10 ticker-scroll-container"
          style={{
            '--ticker-duration': `${duration}s`,
            '--ticker-loop-offset': loopOffset,
            animationPlayState: isPaused ? "paused" : "running",
          } as React.CSSProperties}
        >
          {Array.from({ length: minCopies }, (_, copyIdx) => (
            <div key={`copy-${copyIdx}`} className="inline-flex items-center flex-nowrap">
              {copyIdx > 0 && SEPARATOR}
              <WorldCupCountdownTickerItem />
              {SEPARATOR}
              {renderItems(`c${copyIdx}`)}
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
